import { Controller } from '@hotwired/stimulus';
import Axios from 'axios';
import { Modal } from 'bootstrap';
import { dashboardAction, getMeta } from '../helpers';

export default class extends Controller {
    static targets = ['toast', 'formLink'];

    initialize() {
        this.toastTimeout = null;
        this.variables = {};
        this.mouseDown = (event) => this._mouseDown(event);
        this.formLinkClick = (event) => this._formLinkClick(event);
        this.mouseDownEvent = null;

        super.initialize();
    }

    connect() {
        window.application = this;
        this.element.addEventListener('mousedown', this.mouseDown);
    }

    disconnect() {
        this.element.removeEventListener('mousedown', this.mouseDown);
        super.disconnect();
    }

    _mouseDown(event) {
        this.mouseDownEvent = event;
    }

    toggleSidebar() {
        this.element.classList.toggle('sidebar-toggled');
    }

    formLinkTargetConnected(element) {
        element.addEventListener('click', this.formLinkClick);
    }

    formLinkTargetDisconnected(element) {
        element.removeEventListener('click', this.formLinkClick);
    }

    async _formLinkClick(event) {
        event.preventDefault();
        const formData = new FormData();
        const element = event.currentTarget;
        const params = new URLSearchParams(element.search);

        for (const [key, value] of params) {
            formData.append(key, value);
        }

        const data = this.formDataObject(formData);

        let response;

        try {
            response = await Axios['post'](element.dataset.originalHref, data, {
                headers: {
                    'x-custom-modal': '1',
                    'x-turbo-modal': '1',
                }
            });

        } catch (error) {
            console.error(error);
            response = error.response;
        }

        switch (response.status) {
            case 200:
                this.createModalOfContent(response.data, null);
                break;
            case 303:
                location.href = response.data;
                break;
            default:
                if (response.data.dashboard_toast) {
                    application.toastMessage(
                        response.data.dashboard_toast.dashboard_toast_header,
                        response.data.dashboard_toast.dashboard_toast_description,
                        response.data.dashboard_toast.dashboard_toast_timeout,
                    );
                } else {
                    this.toastGeneralError();
                }
        }
    }

    deepSet(obj, path, value) {
        if (Object(obj) !== obj) {
            return obj;
        }

        if (!Array.isArray(path)) {
            path = path.toString().match(/[^.[\]]+/g) || [];
        }

        path.reduce((acc, curr, index) => {
            if (Object(acc[curr]) !== acc[curr]) {
                acc[curr] = Math.abs(path[index + 1]) >> 0 === +path[index + 1] ? [] : {};
            }

            if (index === path.length - 1) {
                acc[curr] = value;
            }

            return acc[curr];
        }, obj);

        return obj;
    }

    formDataObject(formData) {
        const root = {};
        for (const [path, value] of formData) {
            this.deepSet(root, path, value);
        }
        return root;
    }


    simulateLink(event) {
        // Prevent simulation when click happened while dragging
        const dragThreshold = 20;
        const preventByDrag = this.mouseDownEvent && (Math.abs(this.mouseDownEvent.screenX - event.screenX) > dragThreshold || Math.abs(this.mouseDownEvent.screenY - event.screenY) > dragThreshold);

        if (!preventByDrag && !event.target.closest('A, INPUT, BUTTON, SELECT')) {
            const link = document.createElement('a');
            link.href = event.currentTarget.dataset.link;

            if (event.currentTarget.dataset.turboHeaders) {
                link.dataset.turboHeaders = event.currentTarget.dataset.turboHeaders;
            }

            document.body.appendChild(link);
            link.click();
            link.remove();
        }
    }

    hideToast() {
        if (this.toastTarget) {
            this.toastTarget.classList.remove('show');
        }
    }

    toastGeneralError() {
        this.toastMessage(getMeta('dashboard-general-error'), getMeta('dashboard-general-error-description'));
    }

    setLoading(value = true) {
        if (value) {
            document.documentElement.setAttribute('aria-busy', 'true');
        } else {
            document.documentElement.removeAttribute('aria-busy');
        }
    }

    toastError(message) {
        if (message.dashboard_toast) {
            this.toastMessage(
                message.dashboard_toast.dashboard_toast_header,
                message.dashboard_toast.dashboard_toast_description,
                message.dashboard_toast.dashboard_toast_timeout,
            );
        } else {
            this.toastGeneralError();
        }
    }

    toastMessage(header, description, timeout, success) {
        if (this.toastTarget) {
            if (this.toastTimeout) {
                clearTimeout(this.toastTimeout);
            }

            const alertBody = this.toastTarget.querySelector('.alert-body');
            const alertHeader = this.toastTarget.querySelector('.alert-title');
            const alertDescription = this.toastTarget.querySelector('.alert-description');

            alertBody.classList.remove('alert-success', 'alert-danger');
            alertHeader.classList.remove('d-none');
            alertDescription.classList.remove('d-none');

            if (!header) {
                alertHeader.classList.add('d-none');
            }

            if (!description) {
                alertDescription.classList.add('d-none');
            }

            alertHeader.innerHTML = header;
            alertDescription.innerHTML = description;
            alertBody.classList.add(success ? 'alert-success' : 'alert-danger');
            this.toastTarget.classList.add('show');

            this.toastTimeout = setTimeout(() => {
                this.toastTarget.classList.remove('show');
            }, timeout || 5000);
        }
    }

    async closeModals(amount) {
        const modals = document.body.querySelectorAll('.dashboard-modal.show');

        if (!amount) {
            amount = modals.length;
        }

        for (let i = 0; i < amount; i++) {
            const modal = modals[modals.length - i - 1];

            if (modal) {
                await this._closeModal(modal);
            }
        }
    }

    _closeModal(modalElement) {
        const promise = new Promise((resolve) => {
            const promiseResolve = () => {
                resolve();
                modalElement.removeEventListener('hidden.bs.modal', promiseResolve);
            };

            modalElement.addEventListener('hidden.bs.modal', promiseResolve);
        });

        Modal.getOrCreateInstance(modalElement).hide();

        return promise;
    }

    createModalOfContent(content, instigator) {
        let body = document.createElement('div');
        body.innerHTML = content;
        body = body.firstElementChild;
        body.setAttribute('data-turbo-temporary', 'true');

        const modal = new Modal(body, {
            keyboard: false,
            backdrop: false,
        });

        const originalResetElementAttributes = modal._scrollBar._resetElementAttributes.bind(modal._scrollBar);

        modal.instigator = instigator;

        body.addEventListener('hidden.bs.modal', this._destroyModal);
        body.addEventListener('mousedown', this._hideModal);

        document.body.appendChild(body);
        modal.handleUpdate();
        modal._scrollBar._resetElementAttributes = (selector, styleProperty) => {
            const modals = document.body.querySelectorAll('.dashboard-modal');

            if (modals.length <= 1) {
                document.body.removeAttribute('data-bs-overflow');
                document.body.removeAttribute('data-bs-padding-right');
                originalResetElementAttributes(selector, styleProperty);
            }
        };
        modal.show();
    }

    _destroyModal(event) {
        Modal.getInstance(event.target)?.dispose();
        event.target.remove();
    }

    _hideModal(event) {
        if (event.target.classList.contains('dashboard-modal') && !event.target.dataset.staticBackdrop && event.button === 0) {
            Modal.getInstance(event.target)?.hide();
        }
    }

    changeTheme(event) {
        const checked = event.currentTarget.checked;

        document.body.classList.remove('theme-dark', 'theme-light');
        document.body.classList.add(checked ? 'theme-dark' : 'theme-light');

        Axios['post'](dashboardAction('authorization', 'user', 'setPreferredTheme', [checked ? 'theme-dark' : 'theme-light']));
    }
}
